import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { ShieldCheck, ArrowsClockwise, Check, X } from "@phosphor-icons/react";

const ROLE_LABEL = { admin: "Gérant", associate: "Associé", technician: "Technicien" };
const DOMAIN_LABEL = {
  chantiers: "Chantiers",
  interventions: "Interventions",
  tasks: "Tâches",
  team: "Équipe",
  finance: "Finance & PDF",
  system: "Système",
};

/**
 * Effective permissions of a user = role preset ± overrides.
 * The dialog lets an admin toggle each permission; the diff vs the role preset
 * is stored as ["+extra", "-removed"] in `permissions_override`.
 */
export default function UserPermissionsDialog({ open, onOpenChange, user, onSaved }) {
  const [catalog, setCatalog] = useState(null); // { permissions, role_presets }
  const [role, setRole] = useState(user?.role || "technician");
  const [effective, setEffective] = useState(new Set()); // Set<permKey> currently checked
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setRole(user?.role || "technician");
    api.get("/permissions/catalog").then((r) => {
      setCatalog(r.data);
      const preset = new Set(r.data.role_presets[user?.role] || []);
      // Apply user's saved overrides on top of the preset
      (user?.permissions_override || []).forEach((tok) => {
        if (typeof tok !== "string" || !tok) return;
        if (tok.startsWith("-")) preset.delete(tok.slice(1));
        else if (tok.startsWith("+")) preset.add(tok.slice(1));
        else preset.add(tok);
      });
      setEffective(preset);
    }).catch(() => setCatalog(null));
  }, [open, user]);

  // Compute overrides diff vs the currently-chosen role preset
  const overrides = useMemo(() => {
    if (!catalog) return [];
    const preset = new Set(catalog.role_presets[role] || []);
    const out = [];
    // Grants (in effective, not in preset)
    effective.forEach((p) => { if (!preset.has(p)) out.push(`+${p}`); });
    // Revokes (in preset, not in effective)
    preset.forEach((p) => { if (!effective.has(p)) out.push(`-${p}`); });
    return out;
  }, [effective, role, catalog]);

  const toggle = (perm) => {
    setEffective((s) => {
      const next = new Set(s);
      if (next.has(perm)) next.delete(perm); else next.add(perm);
      return next;
    });
  };

  const applyRolePreset = (newRole) => {
    setRole(newRole);
    // Reset effective to the preset (clears all overrides)
    setEffective(new Set(catalog?.role_presets[newRole] || []));
  };

  const resetToPreset = () => {
    if (!catalog) return;
    setEffective(new Set(catalog.role_presets[role] || []));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/users/${user.id}`, { role, permissions_override: overrides });
      onOpenChange(false);
      setTimeout(() => toast.success("Permissions mises à jour"), 50);
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const nGrants = overrides.filter((t) => t.startsWith("+")).length;
  const nRevokes = overrides.filter((t) => t.startsWith("-")).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg bg-[#111] border-white/10 text-white rounded-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="user-permissions-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-lg flex items-center gap-2">
            <ShieldCheck size={18} weight="duotone" className="text-[#005CFF]" />
            Permissions — {user?.name}
          </DialogTitle>
          <DialogDescription className="text-[11px] uppercase tracking-widest text-white/50">
            Rôle = preset par défaut · cochez pour personnaliser
          </DialogDescription>
        </DialogHeader>

        {/* Role selector */}
        <div className="flex items-center gap-2">
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">Rôle</div>
            <Select value={role} onValueChange={applyRolePreset}>
              <SelectTrigger className="bg-white/5 border-white/10" data-testid="perm-role-select"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#111] border-white/10 text-white">
                {Object.entries(ROLE_LABEL).map(([k, v]) => (<SelectItem key={k} value={k}>{v}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="ghost" onClick={resetToPreset} className="border border-white/10 h-9 self-end" title="Réappliquer les défauts du rôle" data-testid="perm-reset-btn">
            <ArrowsClockwise size={12} className="mr-1" /> Défauts
          </Button>
        </div>

        {/* Overrides summary */}
        {overrides.length > 0 && (
          <div className="rounded-lg bg-black/30 border border-white/5 px-2.5 py-1.5 flex items-center gap-3 text-[10px]" data-testid="perm-overrides-summary">
            <ShieldCheck size={11} className="text-[#7CB2FF]" weight="duotone" />
            <span className="text-white/60">Personnalisé :</span>
            {nGrants > 0 && <span className="text-emerald-400">+{nGrants} accordée{nGrants > 1 ? "s" : ""}</span>}
            {nRevokes > 0 && <span className="text-red-400">−{nRevokes} retirée{nRevokes > 1 ? "s" : ""}</span>}
          </div>
        )}

        {/* Permission groups */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-1 -mr-1">
          {!catalog && <div className="text-xs text-white/50 py-4 text-center">Chargement…</div>}
          {catalog && Object.entries(catalog.permissions).map(([domain, perms]) => (
            <div key={domain}>
              <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1.5">{DOMAIN_LABEL[domain] || domain}</div>
              <div className="space-y-1">
                {Object.entries(perms).map(([key, label]) => {
                  const isOn = effective.has(key);
                  const inPreset = (catalog.role_presets[role] || []).includes(key);
                  const isCustom = isOn !== inPreset;
                  return (
                    <button
                      key={key}
                      onClick={() => toggle(key)}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg border transition-colors text-left ${isOn ? "bg-[#005CFF]/15 border-[#005CFF]/40" : "bg-white/[0.02] border-white/5 hover:border-white/15"}`}
                      data-testid={`perm-toggle-${key}`}
                    >
                      <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 ${isOn ? "bg-[#005CFF]" : "bg-white/10"}`}>
                        {isOn && <Check size={10} weight="bold" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs truncate">{label}</div>
                        <div className="text-[9px] text-white/30 font-mono truncate">{key}</div>
                      </div>
                      {isCustom && (
                        <span className={`text-[8px] uppercase tracking-widest px-1.5 py-0.5 rounded-full shrink-0 ${isOn ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/40" : "bg-red-500/20 text-red-400 border border-red-500/40"}`}>
                          {isOn ? "+ Accordée" : "− Retirée"}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2 border-t border-white/5">
          <Button variant="ghost" className="flex-1 border border-white/10" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving} className="flex-1 bg-[#005CFF] hover:bg-[#0047CC]" data-testid="perm-save-btn">
            {saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
